import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { businessApi, usersApi, subscriptionApi, paymentsApi } from '../lib/api';
import { Settings as SettingsIcon, Palette, Lock, Building2, CreditCard, AlertTriangle, ToggleLeft, ToggleRight, RefreshCcw, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { formatDate, cn } from '../lib/utils';

const FEATURE_INFO = {
    safe_methods: { label: 'Safe Methods', description: 'SFBB documented procedures' },
    daily_diary: { label: 'Daily Diary', description: 'Opening/closing checklists' },
    temperature: { label: 'Temperature Logs', description: 'Fridge & freezer monitoring' },
    allergens: { label: 'Allergens', description: 'Menu items & allergen matrix' },
    suppliers: { label: 'Suppliers', description: 'Supplier management' },
    training: { label: 'Training Records', description: 'Staff training & expiry' },
    incidents: { label: 'Incidents', description: 'Incident reporting & resolution' },
    calibration: { label: 'Probe Calibration', description: 'Thermometer calibration logs' },
    deliveries: { label: 'Delivery Records', description: 'Delivery acceptance checks' },
    labels: { label: 'Storage Labels', description: 'Print food storage labels' },
    reviews: { label: '4-Weekly Review', description: 'Scheduled SFBB reviews' },
    reports: { label: 'Reports', description: 'Compliance reports & PDF export' },
};

export default function Settings() {
    const { business, isManager, isAdmin, loadUser, enabledFeatures } = useAuth();
    const [branding, setBranding] = useState({ primary_color: '#f2c24b', secondary_color: '#0B1120', accent_color: '#1E293B', logo_url: '' });
    const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
    const [subscription, setSubscription] = useState(null);
    const [allFeatures, setAllFeatures] = useState([]);
    const [localFeatures, setLocalFeatures] = useState([]);
    const [savingBrand, setSavingBrand] = useState(false);
    const [savingPwd, setSavingPwd] = useState(false);
    const [buildInfo, setBuildInfo] = useState(null);

    useEffect(() => {
        fetch('/build-info.json?t=' + Date.now()).then(r => r.json())
            .then(d => setBuildInfo(d)).catch(() => {});
    }, []);
    const [savingFeatures, setSavingFeatures] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [renewing, setRenewing] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    // Handle renewal redirect from Stripe
    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        const renewed = searchParams.get('renewed');
        if (sessionId && renewed) {
            const pollRenewal = async (attempts = 0) => {
                try {
                    const { data } = await paymentsApi.getStatus(sessionId);
                    if (data.payment_status === 'paid') {
                        toast.success('Subscription renewed successfully!');
                        subscriptionApi.getDetails().then(({ data: sub }) => setSubscription(sub)).catch(() => {});
                        loadUser();
                    } else if (attempts < 5) {
                        setTimeout(() => pollRenewal(attempts + 1), 2500);
                        return;
                    } else {
                        toast.info('Payment is still being processed. Please refresh in a moment.');
                    }
                } catch {
                    if (attempts < 5) { setTimeout(() => pollRenewal(attempts + 1), 2500); return; }
                }
                setSearchParams({});
            };
            pollRenewal();
        }
    }, [searchParams, setSearchParams, loadUser]);

    useEffect(() => {
        if (business?.branding) {
            setBranding({
                primary_color: business.branding.primary_color || '#f2c24b',
                secondary_color: business.branding.secondary_color || '#0B1120',
                accent_color: business.branding.accent_color || '#1E293B',
                logo_url: business.branding.logo_url || ''
            });
        }
    }, [business]);

    useEffect(() => {
        if (business) {
            subscriptionApi.getDetails().then(({ data }) => setSubscription(data)).catch(() => {});
            businessApi.getFeatures().then(({ data }) => setAllFeatures(data.all_features)).catch(() => {});
        }
    }, [business]);

    useEffect(() => {
        setLocalFeatures(enabledFeatures.length > 0 ? [...enabledFeatures] : [...allFeatures]);
    }, [enabledFeatures, allFeatures]);

    const toggleFeature = (key) => {
        setLocalFeatures(prev =>
            prev.includes(key) ? prev.filter(f => f !== key) : [...prev, key]
        );
    };

    const handleSaveFeatures = async () => {
        setSavingFeatures(true);
        try {
            await businessApi.updateFeatures(localFeatures);
            toast.success('Features updated — menu will refresh');
            loadUser();
        } catch { toast.error('Failed to update features'); }
        finally { setSavingFeatures(false); }
    };

    const featuresChanged = JSON.stringify([...localFeatures].sort()) !== JSON.stringify([...enabledFeatures].sort());

    const handleSaveBranding = async () => {
        setSavingBrand(true);
        try { await businessApi.updateBranding(branding); toast.success('Branding updated'); loadUser(); }
        catch { toast.error('Failed to update branding'); }
        finally { setSavingBrand(false); }
    };

    const handleChangePassword = async () => {
        if (passwords.new_password !== passwords.confirm_password) { toast.error('Passwords do not match'); return; }
        if (passwords.new_password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
        setSavingPwd(true);
        try {
            await usersApi.changePassword({ current_password: passwords.current_password, new_password: passwords.new_password });
            toast.success('Password changed');
            setPasswords({ current_password: '', new_password: '', confirm_password: '' });
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change password'); }
        finally { setSavingPwd(false); }
    };

    const handleCancelSubscription = async () => {
        if (!window.confirm('Are you sure you want to cancel your subscription? Access will continue until the end of your current billing period.')) return;
        setCancelling(true);
        try {
            await subscriptionApi.cancel();
            toast.success('Subscription cancelled');
            subscriptionApi.getDetails().then(({ data }) => setSubscription(data)).catch(() => {});
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to cancel subscription'); }
        finally { setCancelling(false); }
    };

    const handleRenewSubscription = async () => {
        setRenewing(true);
        try {
            const { data } = await subscriptionApi.renew();
            if (data.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                toast.error('Could not create payment session');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to initiate renewal');
        } finally {
            setRenewing(false);
        }
    };

    const statusColors = {
        active: 'bg-emerald-500/20 text-emerald-400',
        trial: 'bg-blue-500/20 text-blue-400',
        pending: 'bg-amber-500/20 text-amber-400',
        cancelled: 'bg-rose-500/20 text-rose-400',
        expired: 'bg-slate-500/20 text-slate-400',
    };

    return (
        <Layout>
            <div data-testid="settings-page">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
                        <SettingsIcon className="h-6 w-6 brand-icon" /> Settings
                    </h1>
                </div>

                <div className="space-y-6 max-w-2xl">
                    {/* Feature Management */}
                    {business && (isManager || isAdmin) && allFeatures.length > 0 && (
                        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5" data-testid="feature-management-section">
                            <h2 className="text-lg font-semibold text-slate-200 flex items-center gap-2 mb-1">
                                <ToggleRight className="h-5 w-5 brand-icon" /> Feature Management
                            </h2>
                            <p className="text-xs text-slate-400 mb-4">Toggle modules on or off for your business. Disabled modules are hidden from the menu for you and your staff.</p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {allFeatures.map(key => {
                                    const info = FEATURE_INFO[key] || { label: key, description: '' };
                                    const enabled = localFeatures.includes(key);
                                    return (
                                        <button key={key} type="button" onClick={() => toggleFeature(key)}
                                            className={cn(
                                                'flex items-center gap-3 p-3 rounded-lg border text-left transition-all',
                                                enabled
                                                    ? 'bg-emerald-500/5 border-emerald-500/30 hover:bg-emerald-500/10'
                                                    : 'bg-slate-800/30 border-slate-700/50 hover:bg-slate-800/60 opacity-60'
                                            )} data-testid={`feature-toggle-${key}`}>
                                            {enabled ? (
                                                <ToggleRight className="h-5 w-5 text-emerald-400 shrink-0" />
                                            ) : (
                                                <ToggleLeft className="h-5 w-5 text-slate-500 shrink-0" />
                                            )}
                                            <div className="min-w-0">
                                                <p className={cn("text-sm font-medium", enabled ? "text-slate-200" : "text-slate-400")}>{info.label}</p>
                                                <p className="text-[11px] text-slate-500 truncate">{info.description}</p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            {featuresChanged && (
                                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-800">
                                    <Button onClick={handleSaveFeatures} disabled={savingFeatures}
                                        className="brand-btn font-medium" data-testid="save-features-button">
                                        {savingFeatures ? 'Saving...' : 'Save Changes'}
                                    </Button>
                                    <Button variant="outline" onClick={() => setLocalFeatures([...enabledFeatures])}
                                        className="border-slate-700 text-slate-300">Reset</Button>
                                    <span className="text-xs brand-text ml-2">Unsaved changes</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Subscription Management */}
                    {business && subscription && isManager && !isAdmin && (
                        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5" data-testid="subscription-section">
                            <h2 className="text-lg font-semibold text-slate-200 flex items-center gap-2 mb-4">
                                <CreditCard className="h-5 w-5 brand-icon" /> Subscription
                            </h2>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                    <div>
                                        <span className="text-sm text-slate-300">Status</span>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusColors[subscription.subscription_status] || statusColors.pending}`}>
                                                {subscription.subscription_status}
                                            </span>
                                            {subscription.auto_renew && subscription.subscription_status === 'active' && (
                                                <span className="text-xs text-slate-500">Auto-renews monthly</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-sm text-slate-300">Monthly Price</span>
                                        <p className="text-xl font-bold brand-text">£{subscription.subscription_price?.toFixed(2)}</p>
                                    </div>
                                </div>
                                {subscription.subscription_end_date && (
                                    <p className="text-xs text-slate-400">
                                        {subscription.subscription_status === 'cancelled' ? 'Access until: ' : 'Renews: '}
                                        <span className="text-slate-300">{formatDate(subscription.subscription_end_date)}</span>
                                    </p>
                                )}
                                {subscription.applied_coupon && <p className="text-xs text-emerald-400">Coupon applied: {subscription.applied_coupon}</p>}
                                {subscription.recent_payments?.length > 0 && (
                                    <div className="mt-3">
                                        <p className="text-xs text-slate-500 font-semibold uppercase mb-2">Recent Payments</p>
                                        <div className="space-y-1.5">
                                            {subscription.recent_payments.slice(0, 3).map((p) => (
                                                <div key={p.id} className="flex items-center justify-between text-xs bg-slate-800/30 rounded px-3 py-2">
                                                    <span className="text-slate-400">{formatDate(p.created_at)}</span>
                                                    <span className="text-slate-300">£{p.amount?.toFixed(2)} {p.currency?.toUpperCase()}</span>
                                                    <span className={p.payment_status === 'paid' ? 'text-emerald-400' : 'text-amber-400'}>{p.payment_status}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {subscription.subscription_status !== 'cancelled' && subscription.subscription_status !== 'expired' && (
                                    <div className="pt-2">
                                        <Button variant="outline" onClick={handleCancelSubscription} disabled={cancelling}
                                            className="border-rose-800 text-rose-400 hover:bg-rose-500/10 text-sm" data-testid="cancel-subscription-button">
                                            <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                                            {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                                        </Button>
                                    </div>
                                )}
                                {(subscription.subscription_status === 'cancelled' || subscription.subscription_status === 'expired' || subscription.subscription_status === 'pending') && (
                                    <div className="pt-2">
                                        <Button onClick={handleRenewSubscription} disabled={renewing}
                                            className="brand-btn font-semibold text-sm" data-testid="renew-subscription-button">
                                            <RefreshCcw className="h-3.5 w-3.5 mr-1" />
                                            {renewing ? 'Processing...' : 'Renew Subscription'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Business Branding */}
                    {business && (
                        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5" data-testid="branding-section">
                            <h2 className="text-lg font-semibold text-slate-200 flex items-center gap-2 mb-4">
                                <Palette className="h-5 w-5 brand-icon" /> Business Branding
                            </h2>
                            <div className="space-y-3">
                                <div className="flex items-center gap-3">
                                    <Building2 className="h-4 w-4 text-slate-500" />
                                    <span className="text-sm text-slate-300">{business.name}</span>
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    {[
                                        { key: 'primary_color', label: 'Primary' },
                                        { key: 'secondary_color', label: 'Secondary' },
                                        { key: 'accent_color', label: 'Accent' },
                                    ].map(({ key, label }) => (
                                        <div key={key}>
                                            <Label className="text-slate-300 text-xs">{label}</Label>
                                            <div className="flex items-center gap-2 mt-1">
                                                <input type="color" value={branding[key]} onChange={(e) => setBranding({ ...branding, [key]: e.target.value })}
                                                    className="h-9 w-12 rounded border border-slate-700 bg-slate-800 cursor-pointer" />
                                                <Input value={branding[key]} onChange={(e) => setBranding({ ...branding, [key]: e.target.value })}
                                                    className="bg-slate-800 border-slate-700 text-slate-100 text-xs h-9" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div>
                                    <Label className="text-slate-300 text-xs">Logo URL</Label>
                                    <Input value={branding.logo_url} onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })}
                                        className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="https://example.com/logo.png" />
                                </div>
                                <Button onClick={handleSaveBranding} disabled={savingBrand} className="brand-btn font-medium" data-testid="save-branding-button">
                                    {savingBrand ? 'Saving...' : 'Save Branding'}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Change Password */}
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5" data-testid="password-section">
                        <h2 className="text-lg font-semibold text-slate-200 flex items-center gap-2 mb-4">
                            <Lock className="h-5 w-5 brand-icon" /> Change Password
                        </h2>
                        <div className="space-y-3">
                            <div>
                                <Label className="text-slate-300">Current Password</Label>
                                <Input type="password" value={passwords.current_password} onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="current-password-input" />
                            </div>
                            <div>
                                <Label className="text-slate-300">New Password</Label>
                                <Input type="password" value={passwords.new_password} onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="new-password-input" />
                            </div>
                            <div>
                                <Label className="text-slate-300">Confirm New Password</Label>
                                <Input type="password" value={passwords.confirm_password} onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="confirm-password-input" />
                            </div>
                            <Button onClick={handleChangePassword} disabled={savingPwd || !passwords.current_password || !passwords.new_password}
                                className="brand-btn font-medium" data-testid="change-password-button">
                                {savingPwd ? 'Changing...' : 'Change Password'}
                            </Button>
                        </div>
                    </div>

                    {/* App Info */}
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5" data-testid="app-info-section">
                        <h2 className="text-lg font-semibold text-slate-200 flex items-center gap-2 mb-3">
                            <Info className="h-5 w-5 brand-icon" /> App Info
                        </h2>
                        {buildInfo && (
                            <div className="space-y-2 text-sm">
                                <div className="flex items-center justify-between">
                                    <span className="text-slate-400">App Version</span>
                                    <span className="text-slate-200 font-mono select-all" data-testid="app-version">{buildInfo.version}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-slate-400">Build Date</span>
                                    <span className="text-slate-200 text-xs" data-testid="app-build-date">{buildInfo.display}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}
